import { useCallback, useMemo, useState } from 'react'

export interface ReviewEntriesSelection {
  areAllEntriesSelected: boolean
  clearSelection: () => void
  selectedEntryIds: string[]
  toggleAllEntries: () => void
  toggleEntry: (entryId: string) => void
}

// selection is keyed by entry id and scoped to the currently selectable (auto-validatable)
// entries — ids that drop out of the selectable set (validated, filtered out) are pruned
export const useReviewEntriesSelection = (selectableEntryIds: string[]): ReviewEntriesSelection => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const selectedEntryIds = useMemo(
    () => selectedIds.filter((id) => selectableEntryIds.includes(id)),
    [selectedIds, selectableEntryIds],
  )

  const areAllEntriesSelected = selectableEntryIds.length > 0 && selectedEntryIds.length === selectableEntryIds.length

  const toggleEntry = useCallback((entryId: string) => {
    setSelectedIds((current) =>
      current.includes(entryId) ? current.filter((id) => id !== entryId) : [...current, entryId],
    )
  }, [])

  const toggleAllEntries = useCallback(() => {
    setSelectedIds(areAllEntriesSelected ? [] : selectableEntryIds)
  }, [areAllEntriesSelected, selectableEntryIds])

  const clearSelection = useCallback(() => {
    setSelectedIds([])
  }, [])

  return { areAllEntriesSelected, clearSelection, selectedEntryIds, toggleAllEntries, toggleEntry }
}
